import { ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Udf } from '@core/Udf';
import { TranslateService } from '@ngx-translate/core';
import { DropdownService } from '@shared/dropdown/dropdown.service';
import { OutsideClickDirective } from '@ui-kit/directives/outside-click/outside-click.directive';
import { format, isValid, parse } from 'date-fns';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

@Injectable()
export class MyDateAdapter extends NativeDateAdapter {
  // Datepicker starts with monday
  getFirstDayOfWeek(): number {
    return 1;
  }

  getYear(date) {
    return date?.getFullYear();
  }
  getMonth(date) {
    return date?.getMonth();
  }
  getDate(date) {
    return date?.getDate();
  }
  // getDayOfWeek(date) {
  //   return date.getDay();
  // }
}

@Component({
  selector: 'app-datepicker',
  templateUrl: './app-datepicker.component.html',
  styleUrls: ['./app-datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    OutsideClickDirective,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDatepickerComponent),
      multi: true
    },
    { provide: DateAdapter, useClass: MyDateAdapter }
  ]
})
export class AppDatepickerComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  private _inputDateFormat: string = 'YYYY-MM-DD';
  @Input() set date(data) {
    this._date = data ? format(legacyParse(data), convertTokens(this._inputDateFormat)) : '';
  }
  @Input() set minDate(data) {
    this._minDate = data ? format(legacyParse(data), convertTokens(this._inputDateFormat)) : '';
  }
  @Input() height: string;
  @Input() set disabled(val) {
    setTimeout(() => {
      this.setDisabledState(val);
    }, 16);
  }
  @Input() datepickerIcon: string = 'icon-Calendar_ico';
  @Input() placeholder: string;
  @Input() rangeMode: boolean;
  @Input() resetControl: UntypedFormControl;
  @Input() isResetShowed = false;
  @Input() hasBackdrop = false;
  @Input() set maxDate(date) {
    this.maxDateToSelect = date;
  }
  @Input() dropdownPosition: ConnectedPosition[] = [
    {
      originX: 'start', // left corner of the button
      originY: 'bottom', // bottom corner of the button
      overlayX: 'start', // left corner of the overlay to the origin
      overlayY: 'top', // top corner of the overlay to the origin
      offsetY: 5
    },
    {
      originX: 'start', // left corner of the button
      originY: 'top', // top corner of the button
      overlayX: 'start', // left corner of the overlay to the origin
      overlayY: 'bottom', // top corner of the overlay to the origin
      offsetY: -35
    },
    {
      originX: 'end', // left corner of the button
      originY: 'bottom', // bottom corner of the button
      overlayX: 'end', // left corner of the overlay to the origin
      overlayY: 'top', // top corner of the overlay to the origin
      offsetY: 5
    },
    {
      originX: 'end', // left corner of the button
      originY: 'top', // top corner of the button
      overlayX: 'end', // left corner of the overlay to the origin
      overlayY: 'bottom', // top corner of the overlay to the origin
      offsetY: -35
    }
  ];
  @Output() dateChanged = new EventEmitter<string>();
  @Output() dropdownOpened = new EventEmitter<any>();
  @Output() dropdownClosed = new EventEmitter<any>();
  private _dateValueFormat: string = 'DD.MM.YYYY';
  datepickerOptions: any = {
    format: this._dateValueFormat
  };
  _date;
  _minDate;
  maxDateToSelect: string;
  dateControl = new UntypedFormControl();

  selectedMonth: Date;

  private destroyed$ = new Subject();

  private overlayRef: Udf<OverlayRef>;

  @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;
  @ViewChild('dateInput', { static: false }) dateInput: ElementRef;

  @HostListener('document:keydown.tab')
  onKeydownTab() {
    if (this.isOpen) {
      this.onTouchedCallback();
      this.overlayRef.dispose();
      this.isFocused = false;
      return;
    }
  }

  selectedElement: any = null;
  isFocused: boolean = false;
  onFocus(event) {
    this.isFocused = true;
    if (event) {
      this.selectedElement = event.target;
    }
  }

  // form view adapter implementation
  public onChangeCallback = (_: any) => undefined;

  public onTouchedCallback = () => undefined;

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: any): void {
    const newV = value?.length === 0 ? this._date : value;
    if (newV) {
      this.dateControl.setValue(format(legacyParse(new Date(newV)), convertTokens(this._dateValueFormat)));
    }
  }

  parseCalendarDate(input): null | Date {
    if (!input || input === 'Invalid Date') {
      return null;
    }

    const parts = input.match(/(\d+)/g);
    // note parts[1]-1
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  isValid(stringDate): boolean {
    if (stringDate === '' || stringDate === null || stringDate.length < 10) {
      return false;
    }

    const parts = stringDate.match(/(\d+)/g);

    const day = parts[0];
    const month = parts[1];
    const year = parts[2];

    const parsed = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date());
    return isValid(legacyParse(parsed));
  }

  changed(date: any, eventType): void | boolean {
    let newData;

    // @ts-ignore
    if (eventType) {
      const isVal = this.isValid(date);

      if (!isVal && date !== '') {
        return;
      } else if (date === '') {
        this.onTouchedCallback();
        this.onChangeCallback('');
        this.dateChanged.next('');
        this.dropdownClosed.emit();
        return;
      }
    }

    newData = format(
      legacyParse(date instanceof Date ? date : this.parseCalendarDate(date)),
      convertTokens(this._inputDateFormat)
    );

    this.onTouchedCallback();
    this.onChangeCallback(newData);
    this.dateChanged.next(newData);
    this.dropdownClosed.emit();

    if (this.calendar) {
      this.calendar.activeDate = this.parseCalendarDate(this.dateControl.value);
      this.calendar.updateTodaysDate();
    }

    if (!eventType) {
      this.closeDatepicker(new Event('mouseclick'));
      this.dateControl.patchValue(format(legacyParse(newData), convertTokens(this._dateValueFormat)), { emitEvent: false });
    }

    setTimeout(() => {
      this.selectedElement.focus();
    });
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }

  openOnEnterDatepicker(e, template): void {
    if (this.isFocused) {
      this.openDatepicker(e, template);
    }
  }

  openDatepicker(e, template) {
    e.preventDefault();

    if (this.isOpen) {
      this.closeDatepicker();
      return;
    }

    this._adapter.setLocale(this._translate.currentLang);

    this.overlayRef = this._dropdownService.open({
      template: template,
      containerRef: this._viewContainerRef,
      position: this.dropdownPosition,
      hasBackdrop: this.hasBackdrop
    });

    setTimeout(() => {
      const data = this.dateControl.value;
      const isVal = this.isValid(this.dateControl.value);

      if (isVal) {
        this.setCalendarActiveDay(data);
      }
    }, 50);

    this.dropdownOpened.emit();
    // setTimeout(() => {
    //   this._appRef.tick();
    // }, 160)
  }

  private setCalendarActiveDay(data): void {
    console.log('setCalendarActiveDay');
    if (this.calendar) {
      this.calendar.activeDate = this.parseCalendarDate(data);
      this._cdr.markForCheck();
      this._appRef.tick();
    } else {
      // setTimeout(() => {
      //   this.setCalendarActiveDay(data);
      //   this._cdr.markForCheck();
      // }, 200)
    }
  }

  get isOpen() {
    return this.overlayRef && this.overlayRef.hasAttached();
  }

  closeDatepicker(e?): void {
    if (this.isOpen && e) {
      e.preventDefault();
      e.stopPropagation();
      this.onTouchedCallback();
      this.overlayRef.dispose();
      this.dropdownClosed.emit();
      this.isFocused = false;
    }
  }

  private setDateValue(date): void {
    const newData = format(legacyParse(this.parseCalendarDate(date)), convertTokens(this._inputDateFormat));

    this.onTouchedCallback();
    this.onChangeCallback(newData);
    this.dateChanged.next(newData);
  }

  ngOnInit() {
    this._translate.onLangChange.pipe(takeUntil(this.destroyed$)).subscribe((a) => {
      this._adapter.setLocale(this._translate.currentLang);
    });

    const date = this._date ? format(legacyParse(this._date), convertTokens(this._dateValueFormat)) : this._date;

    if (this._date && this._date.length > 0 && !this.rangeMode) {
      this.dateControl.setValue(date);
    }

    this.dateControl.valueChanges.pipe(filter(() => this.isFocused)).subscribe((dateVal: string) => {
      if (dateVal.length === 10 && this.isValid(dateVal) && !this.calendar) {
        this.setDateValue(dateVal);
      }

      if (dateVal.length === 10 && this.isValid(dateVal) && this.calendar) {
        this.calendar.activeDate = this.parseCalendarDate(dateVal);
        this.setDateValue(dateVal);
      } else if (dateVal === '') {
        this.onTouchedCallback();
        this.onChangeCallback('');
        this.dateChanged.next('');
      }
    });
  }

  ngOnDestroy(): void {
    this.closeDatepicker(new Event('mouseclick'));
    this.destroyed$.next(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date']) {
      const date = changes.date.currentValue
        ? format(legacyParse(changes.date.currentValue), convertTokens(this._dateValueFormat))
        : this._date;

      this.dateControl.setValue(date);
    }
  }

  constructor(
    private _adapter: DateAdapter<Date>,
    private _translate: TranslateService,
    private _cdr: ChangeDetectorRef,
    private _appRef: ApplicationRef,
    private _viewContainerRef: ViewContainerRef,
    private _dropdownService: DropdownService
  ) {}
}
